import { gql } from '@apollo/client';
import groupBy from 'lodash/groupBy';
import { useMemo } from 'react';
import {
  DayTypeDetailsFragment,
  RouteDirectionEnum,
  VehicleJourneyWithStartTimeInfoFragment,
  useGetVehicleScheduleFrameSchedulesQuery,
} from '../../../../generated/graphql';
import { TimetablePriority } from '../../../../types/enums';
import { VehicleServiceRowData } from '../../VehicleScheduleDetails';

const GQL_VEHICLE_SCHEDULE_FRAME_SCHEDULES = gql`
  query GetVehicleScheduleFrameSchedules($vehicle_schedule_frame_id: uuid!) {
    timetables {
      timetables_vehicle_schedule_vehicle_schedule_frame_by_pk(
        vehicle_schedule_frame_id: $vehicle_schedule_frame_id
      ) {
        vehicle_schedule_frame_id
        validity_start
        validity_end
        priority
        created_at
        vehicle_services {
          vehicle_service_id
          day_type {
            ...DayTypeDetails
          }
          blocks {
            block_id
            vehicle_journeys {
              ...VehicleJourneyWithStartTimeInfo
            }
          }
        }
      }
    }
  }

  fragment DayTypeDetails on timetables_service_calendar_day_type {
    day_type_id
    label
    name_i18n
  }

  fragment VehicleJourneyWithStartTimeInfo on timetables_vehicle_journey_vehicle_journey {
    vehicle_journey_id
    start_time
    journey_pattern_ref {
      journey_pattern_ref_id
      journey_pattern_instance {
        journey_pattern_id
        journey_pattern_route {
          route_id
          unique_label
          direction
          name_i18n
        }
      }
    }
  }
`;

export type RouteTimetableRowInfo = {
  readonly direction: RouteDirectionEnum;
  readonly nameI18n: LocalizedString;
  readonly label: string;
  readonly vehicleServiceRowData: ReadonlyArray<VehicleServiceRowData>;
  readonly vehicleJourneys: ReadonlyArray<VehicleJourneyWithStartTimeInfoFragment>;
  readonly priority: TimetablePriority;
  readonly vehicleScheduleFrameId: UUID;
  readonly validity: {
    readonly validityStart: Temporal.PlainDate;
    readonly validityEnd: Temporal.PlainDate;
  };
};

type JourneysGroupedByLabelAndDirection = {
  [key: string]: {
    [key: string]: {
      journeys: VehicleJourneyWithStartTimeInfoFragment[];
      nameI18n: LocalizedString;
    };
  };
};

function groupJourneysByLabelAndDirection(
  journeys: ReadonlyArray<VehicleJourneyWithStartTimeInfoFragment>,
) {
  return journeys.reduce((acc: JourneysGroupedByLabelAndDirection, item) => {
    const updatedAcc = { ...acc };
    const route =
      item.journey_pattern_ref.journey_pattern_instance?.journey_pattern_route;

    if (!route) {
      return updatedAcc;
    }
    const { direction, unique_label: label, name_i18n: nameI18n } = route;

    if (!updatedAcc[label]) {
      updatedAcc[label] = {};
    }
    if (!updatedAcc[label][direction]) {
      updatedAcc[label][direction] = {
        journeys: [],
        nameI18n: nameI18n ?? {},
      };
    }
    updatedAcc[label][direction]?.journeys.push(item);

    return updatedAcc;
  }, {});
}

function createTimetableRowInfo(
  allJourneys: ReadonlyArray<VehicleJourneyWithStartTimeInfoFragment>,
  priority: TimetablePriority,
  vehicleScheduleFrameId: UUID,
  validityStart: Temporal.PlainDate,
  validityEnd: Temporal.PlainDate,
): Array<RouteTimetableRowInfo> {
  const timetableRowInfo: RouteTimetableRowInfo[] = [];

  const journeysGroupedByLabelAndDirection =
    groupJourneysByLabelAndDirection(allJourneys);

  Object.keys(journeysGroupedByLabelAndDirection).forEach((routeLabel) => {
    Object.keys(journeysGroupedByLabelAndDirection[routeLabel]).forEach(
      (directionKey) => {
        const direction = directionKey as RouteDirectionEnum;
        const { journeys, nameI18n } =
          journeysGroupedByLabelAndDirection[routeLabel][direction];

        const groupedStartTimes = groupBy(
          journeys
            .map((journey) => journey.start_time)
            .sort((time1, time2) => Temporal.Duration.compare(time1, time2)),
          (item) => item.hours,
        );

        const vehicleServiceRowData: VehicleServiceRowData[] = Object.entries(
          groupedStartTimes,
        ).map(([key, value]) => ({
          hours: Number(key),
          minutes: value.map((item) => item.minutes),
        }));

        timetableRowInfo.push({
          direction,
          nameI18n,
          label: routeLabel,
          vehicleServiceRowData,
          vehicleJourneys: journeys,
          priority,
          vehicleScheduleFrameId,
          validity: {
            validityStart,
            validityEnd,
          },
        });
      },
    );
  });

  return timetableRowInfo;
}

type VehicleScheduleFrameSchedulesFound = {
  readonly timetableRowInfo: ReadonlyArray<RouteTimetableRowInfo>;
  readonly dayType: DayTypeDetailsFragment;
  readonly createdAt: Temporal.ZonedDateTime;
  readonly validityStart: Temporal.PlainDate;
  readonly validityEnd: Temporal.PlainDate;
};

type VehicleScheduleFrameSchedulesNotFound = {
  readonly [Key in keyof VehicleScheduleFrameSchedulesFound]: null;
};

export function useVehicleScheduleFrameSchedules(
  vehicleScheduleFrameId: UUID,
): VehicleScheduleFrameSchedulesFound | VehicleScheduleFrameSchedulesNotFound {
  const { data } = useGetVehicleScheduleFrameSchedulesQuery({
    variables: { vehicle_schedule_frame_id: vehicleScheduleFrameId },
  });

  const vehicleScheduleFrame =
    data?.timetables?.timetables_vehicle_schedule_vehicle_schedule_frame_by_pk;

  return useMemo(() => {
    if (!vehicleScheduleFrame) {
      return {
        timetableRowInfo: null,
        dayType: null,
        createdAt: null,
        validityStart: null,
        validityEnd: null,
      };
    }

    // One vsf can have only one day type, so we can use any of the vehicle services day type
    const dayType = vehicleScheduleFrame.vehicle_services[0].day_type;
    const {
      priority,
      created_at: createdAt,
      validity_start: validityStart,
      validity_end: validityEnd,
      vehicle_services: vehicleServices,
    } = vehicleScheduleFrame;

    const allJourneys = vehicleServices.flatMap((vs) =>
      vs.blocks.flatMap((block) =>
        block.vehicle_journeys.map((journey) => journey),
      ),
    );

    const timetableRowInfo = createTimetableRowInfo(
      allJourneys,
      priority,
      vehicleScheduleFrameId,
      validityStart,
      validityEnd,
    );

    return { timetableRowInfo, dayType, createdAt, validityStart, validityEnd };
  }, [vehicleScheduleFrame, vehicleScheduleFrameId]);
}
